import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCommonModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { fuseAnimations } from 'src/@fuse/animations';
import { FuseConfigService } from 'src/@fuse/services/config.service';
import { FuseSharedModule } from 'src/@fuse/shared.module';
import { cedulaValidator, COLORES, FRUTAS, MASCOTAS } from 'src/app/shared';
import { ForgotPasswordConstans } from 'src/app/shared/constants/forgot-password.constants';

import { IdentificationResponse } from '../../services/microservices/response/identification.response';
import { MicroResponse } from '../../services/microservices/response/micro.response';
import { SimpleResponse } from '../../services/microservices/response/simple.response';
import { UserService } from '../../services/user.service';
import { SessionService } from '../../services/user/session/session.service';
import { DataToForgotPassword } from './object-type/DataToForgotPassword';
import { LoadingComponent } from '../../generals/loading/loading.component';
import { WellcomeComponent } from '../../generals/wellcome/wellcome.component';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatMomentDateModule,
    MatTooltipModule,
    FuseSharedModule,
    FlexLayoutModule,
    RouterLink,
    NgxMatSelectSearchModule,
    WellcomeComponent,
    LoadingComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class ForgotPasswordComponent implements OnInit {
  email = '';
  cardId = '';
  cardIdFormatted = '';
  userData!: string;
  colores: any[];
  mascotas: any[];
  frutas: any[];
  forgotPasswordForm: FormGroup;
  recoverForm: FormGroup = this.getRecoverForm();
  isForm: boolean;
  isFiltroSeguridad!: boolean;
  isVerifyFilters!: boolean;
  isSendMail = false;
  isFilterReset!: boolean;
  isForgotPassword!: boolean;
  isBlocked!: boolean;
  isLogin!: boolean;
  isSesionReciente!: boolean;
  loading = false;
  isFinished = false;

  constructor(
    private _fuseConfigService: FuseConfigService,
    private _formBuilder: FormBuilder,
    private _snackBar: MatSnackBar,
    private _userService: UserService,
    private _router: Router,
    private _sessionService: SessionService,
  ) {
    this._fuseConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        toolbar: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        sidepanel: {
          hidden: true,
        },
      },
    };
    this.forgotPasswordForm = this._formBuilder.group({
      cedula: [
        '',
        [
          cedulaValidator,
          Validators.required,
          Validators.pattern('^[0-9]*$'),
          Validators.minLength(10),
          Validators.maxLength(10),
        ],
      ],
      color: ['', Validators.required],
      mascota: ['', Validators.required],
      fruta: ['', Validators.required],
    });
    this.isForm = true;
    this.colores = COLORES;
    this.mascotas = MASCOTAS;
    this.frutas = FRUTAS;
  }

  ngOnInit(): void {
    this.isFiltroSeguridad = true;
    this._sessionService.dataLoginForgot$.subscribe(data => {
      sessionStorage.setItem('dataLoginUser', JSON.stringify(data));

      // Si se quiere omitir la creacion del dataLoginUser se debe tener en cuenta que es importante
      // para los 3 intentos de filtros
      const element = JSON.parse(
        sessionStorage.getItem('dataLoginUser') as any,
      );
      this._userService.getUserByEmail(element?.username).subscribe({
        next: response => {
          this._userService
            .getIdentificationById(response.data.identificationId)
            .subscribe({
              next: res => {
                this.isFinished = true;
                this.cardId = res.data.cardId;
                const cardIdCrypt =
                  'X'.repeat(this.cardId.length - 4) + this.cardId.slice(-4);
                this.cardIdFormatted = `${cardIdCrypt.slice(0, -1)}-${cardIdCrypt.slice(-1)}`;
              },
            });
        },
        error: e => {
          console.error(e);
        },
      });
    });
  }

  checkCedula(): void {
    const control = this.forgotPasswordForm['controls']['cedula'];
    const valor = control.value;
    if (valor.length === 10 && control.valid) {
      this.buscarPerfilUsuario(valor);
    }
  }

  buscarPerfilUsuario(cedula: string): void {
    this._userService.searchCedula(cedula).subscribe({
      next: (response: MicroResponse<IdentificationResponse>) => {
        if (response === null) {
          this.isFiltroSeguridad = false;
          this.openSnack('No se encontró la cédula');
        } else {
          this.isFiltroSeguridad = true;
        }
      },
      error: () => {
        this.isFiltroSeguridad = false;
        this.openSnack('Ocurrió algo');
      },
    });
  }

  formValid(): boolean {
    return (
      this.forgotPasswordForm.get('color')?.status === 'VALID' &&
      this.forgotPasswordForm.get('mascota')?.status === 'VALID' &&
      this.forgotPasswordForm.get('fruta')?.status === 'VALID' &&
      this.isFinished
    );
  }

  onSubmit() {
    this.loading = true;
    const filtersForm = {
      color: this.forgotPasswordForm.get('color')?.value,
      pet: this.forgotPasswordForm.get('mascota')?.value,
      fruit: this.forgotPasswordForm.get('fruta')?.value,
    };

    this._userService.verifyFilters(this.cardId, filtersForm).subscribe({
      next: (response: MicroResponse<SimpleResponse>) => {
        switch (response.data.id) {
          case ForgotPasswordConstans.FILTERS_OK:
            this.sendEmail(this.cardId);
            break;
          case ForgotPasswordConstans.ENTER_FILTERS_AGAIN:
            this.updateDataToForgotPassword(1);
            break;
          case ForgotPasswordConstans.USER_IS_LOCKED:
            this.updateDataToForgotPassword(2);
            break;
          case ForgotPasswordConstans.USER_WAS_NOT_PROCESSED:
            this.openSnack('La información no fue procesada');
            break;
        }
      },
      error: () => {
        this.openSnack('Ocurrio algo');
      },
    });
  }

  sendEmail(cardId: string): void {
    this._userService.searchCedula(cardId).subscribe({
      next: (response: MicroResponse<IdentificationResponse>) => {
        if (response === null) {
          this.openSnack('Cédula no encontrada');
        } else {
          this._userService
            .buscarPerfilUsuario(response.data.id)
            .subscribe(userData => {
              this.email = userData.data.email;
              this._userService.sendEmail(cardId).subscribe({
                next: () => {
                  this.loading = false;
                  this.isFinished = false;
                  this.isFiltroSeguridad = false;
                  this.isSendMail = true;
                  this.checkStatusUser();
                },
              });
            });
        }
      },
      error: () => {
        this.openSnack('Ocurrió algo');
      },
    });
  }

  getRecoverForm(): FormGroup {
    return this._formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      pinCode: ['', [Validators.required, Validators.minLength(6)]],
      cardId: ['', [Validators.minLength(10), Validators.maxLength(10)]],
      verifedPinCode: [false, Validators.requiredTrue],
    });
  }

  private updateDataToForgotPassword(status: number) {
    this.loading = false;
    const dataToFPStr = sessionStorage.getItem('dataLoginUser');
    if (dataToFPStr) {
      const dataToFP: DataToForgotPassword = JSON.parse(dataToFPStr);
      dataToFP.status.id = status;
      sessionStorage.setItem('dataLoginUser', JSON.stringify(dataToFP));
    }
    this._router.navigate(['/auth/login']);
    if (status == 2) {
      this.isForgotPassword = false;
      this.isLogin = true;
      this.isSesionReciente = false;
      this.isBlocked = true;
    } else {
      this.isForgotPassword = true;
      this.isLogin = true;
      this.isSesionReciente = false;
      this.isBlocked = false;
    }
    this._sessionService.updateForgotPasswordStatus(
      this.isForgotPassword,
      this.isLogin,
      this.isSesionReciente,
      this.isBlocked,
    );
    this._snackBar.open('Filtros fallidos', '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-info'],
      duration: 10000,
    });
  }

  private checkStatusUser() {
    const intervalo = setInterval(() => {
      this._userService.getValidationStatus(this.email).subscribe({
        next: (response: any) => {
          if (response.data === true) {
            this.isFilterReset = response.data;
            clearInterval(intervalo);
          }
        },
      });
    }, 7000);
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
    });
  }

  hasCedulaError(): boolean | undefined {
    const cedulaControl = this.forgotPasswordForm.get('cedula');
    return (
      cedulaControl?.invalid && (cedulaControl?.touched || cedulaControl?.dirty)
    );
  }

  hasColorError(): boolean | undefined {
    const colorControl = this.forgotPasswordForm.get('color');
    return (
      colorControl?.invalid && (colorControl?.touched || colorControl?.dirty)
    );
  }

  hasMascotaError(): boolean | undefined {
    const mascotaControl = this.forgotPasswordForm.get('mascota');
    return (
      mascotaControl?.invalid &&
      (mascotaControl?.touched || mascotaControl?.dirty)
    );
  }

  hasFrutaError(): boolean | undefined {
    const frutaControl = this.forgotPasswordForm.get('fruta');
    return (
      frutaControl?.invalid && (frutaControl?.touched || frutaControl?.dirty)
    );
  }

  getCedulaErrorTooltip(): string {
    const cedulaControl = this.forgotPasswordForm.get('cedula');
    if (cedulaControl?.hasError('required')) {
      return 'Cedula es requerida';
    } else if (cedulaControl?.hasError('pattern')) {
      return 'Cedula no valida';
    } else if (cedulaControl?.hasError('minlength')) {
      return 'Cedula debe tener 10 digitos';
    } else if (cedulaControl?.hasError('maxlength')) {
      return 'Cedula debe tener 10 digitos';
    } else if (cedulaControl?.hasError('cedulaNoValida')) {
      return 'Cedula no tiene estructura valida';
    } else if (cedulaControl?.hasError('cedulaNoEncontrada')) {
      return 'Cedula no encontrada';
    } else if (cedulaControl?.hasError('cedulaExiste')) {
      return 'Este numero de cedula ya es usuario de idempier';
    } else if (cedulaControl?.hasError('prospectoExiste')) {
      return 'Este número de cédula ya está registrado';
    }
    return '';
  }

  getColorErrorTooltip(): string {
    const colorControl = this.forgotPasswordForm.get('color');
    if (colorControl?.hasError('required')) {
      return 'Color es requerido';
    }
    return '¡No reveles tu Color!';
  }

  getMascotaErrorTooltip(): string {
    const mascotaControl = this.forgotPasswordForm.get('mascota');
    if (mascotaControl?.hasError('required')) {
      return 'Mascota es requerido';
    }
    return '¡No reveles tu Mascota!';
  }

  getFrutaErrorTooltip(): string {
    const frutaControl = this.forgotPasswordForm.get('fruta');
    if (frutaControl?.hasError('required')) {
      return 'Fruta es requerido';
    }
    return '¡No reveles tu Fruta!';
  }

  ngDestroy() {
    const dataStorage = JSON.parse(
      sessionStorage.getItem('dataLoginUser') || '',
    );
    this._sessionService.restoreDataLoginForgot(dataStorage);
  }
}
